// Home / dashboard. Self-fetches its own small data sets; no bulk array dep.

const DASHBOARD_STAGE_HUE = {
  prospect: 'slate', qualified: 'slate', interested: 'teal',
  ds_call: 'teal', hot_lead: 'teal', blast: 'violet', bs_call: 'violet',
  data_gathering: 'amber', valuation_presented: 'amber',
  loi: 'blue', won: 'green',
  // Formal sell-side process (082) — hues mirror the equivalent buyside step.
  ss_prospect: 'slate', ss_qualified: 'slate', ss_interested: 'teal',
  ss_valuation: 'amber', ss_pitch: 'teal', ss_cim_prep: 'amber',
  ss_buyer_outreach: 'violet', ss_ioi: 'blue', ss_loi: 'blue',
  ss_mgmt_presentation: 'blue', ss_won: 'green',
};

const REMINDER_LABEL = {
  unanswered_inbound: 'unanswered inbound thread',
  gone_quiet: 'thread went quiet after you wrote last',
};

// ── Pinned analytics dashboard ────────────────────────────────────────────────
// Home embeds ONE real analytics dashboard — the actual DashboardCanvas from
// the Analytics page, in embedded (view-only) mode. The pick is per-browser,
// like `atlas:engagements-groupby` and the per-dashboard filter selections.
const HOME_DASH_KEY = 'atlas:home-dashboard';

// A broken dashboard must never take Home down with it — WidgetRenderer already
// never crashes a dashboard; this is the same guarantee one level up, around
// the whole section.
class HomeDashboardBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) {
      return (
        <div style={{ padding: '16px 14px', fontSize: 12, color: 'var(--text-3)' }}>
          The pinned dashboard failed to render.{' '}
          <button onClick={this.props.onUnpin}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, padding: 0 }}>
            Unpin it
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const HomeDashboard = ({ setView }) => {
  const [pinnedId, setPinnedId] = React.useState(() => {
    try { return localStorage.getItem(HOME_DASH_KEY) || null; } catch (_) { return null; }
  });
  const [dashes, setDashes] = React.useState(null);   // visible dashboards — picker options + name lookup
  const [changing, setChanging] = React.useState(false);

  React.useEffect(() => {
    if (!window.AtlasApi?.analytics?.listDashboards) { setDashes([]); return; }
    window.AtlasApi.analytics.listDashboards()
      .then((d) => setDashes(Array.isArray(d) ? d : []))
      .catch(() => setDashes([]));
  }, []);

  const pin = (id) => {
    try { if (id) localStorage.setItem(HOME_DASH_KEY, id); else localStorage.removeItem(HOME_DASH_KEY); } catch (_) { /* ignore */ }
    setPinnedId(id || null);
    setChanging(false);
  };

  const pinned = pinnedId ? (dashes || []).find((d) => String(d.id) === String(pinnedId)) : null;
  // Deep-link into the full canvas: setting the hash fires hashchange → App
  // lands on Analytics, whose initializer re-opens this dashboard.
  const openFull = () => { window.location.hash = '#/analytics/dashboards/' + encodeURIComponent(pinnedId); };

  const picker = dashes === null ? <Skeleton w={240} h={26} /> : dashes.length === 0 ? (
    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
      No dashboards you can see yet — build one on the{' '}
      <button onClick={() => setView('analytics')}
        style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, padding: 0 }}>
        Analytics
      </button> page first.
    </span>
  ) : (
    <Combobox value={pinnedId || ''} onChange={(e) => { if (e.target.value) pin(e.target.value); }}
      placeholder="Choose a dashboard…"
      options={(dashes || []).map((d) => ({ value: d.id, label: d.group_name ? `${d.group_name} / ${d.name}` : d.name }))}
      style={{ width: 280 }} />
  );

  // Empty state — nothing pinned yet: a subtle card with the picker.
  if (!pinnedId) {
    return (
      <Section title="Pinned dashboard">
        <div style={{ padding: '16px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ width: 24, height: 24, borderRadius: 7, background: HUE.violet.bg, color: HUE.violet.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon.ChartBar size={13} />
          </span>
          {picker}
        </div>
      </Section>
    );
  }

  return (
    <Section
      title={pinned ? `Pinned: ${pinned.name}` : 'Pinned dashboard'}
      action={(
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {changing && picker}
          <Btn size="xs" variant="ghost" onClick={() => setChanging((v) => !v)}>{changing ? 'Cancel' : 'Change'}</Btn>
          <Btn size="xs" variant="ghost" onClick={() => pin(null)}>Unpin</Btn>
          <Btn size="xs" variant="ghost" onClick={openFull} icon={<Icon.External size={11} />}>Open</Btn>
        </span>
      )}
    >
      <HomeDashboardBoundary onUnpin={() => pin(null)}>
        {window.DashboardCanvas ? (
          <DashboardCanvas key={pinnedId} dashId={pinnedId} embedded />
        ) : (
          <div style={{ padding: '16px 14px', fontSize: 12, color: 'var(--text-3)' }}>Dashboards aren't available right now.</div>
        )}
      </HomeDashboardBoundary>
    </Section>
  );
};

const Home = ({ stages, activeUser, counts, setView, setDealId, setWorkspaceId, onOpenStage, onOpenDealsTab }) => {
  const myName = activeUser?.full_name;
  const [workspaces,   setWorkspaces]   = React.useState(null);
  const [pipeline,     setPipeline]     = React.useState(null);   // per-stage aggregate for the chart + EV
  const [alertDeals,   setAlertDeals]   = React.useState(null);   // alerts feed
  const [recentDeals,  setRecentDeals]  = React.useState(null);   // ongoing deals, latest touch first
  const [dealTab,      setDealTab]      = React.useState('active'); // 'active' | 'attention'
  const [dealSide,     setDealSide]     = React.useState('seller'); // 'seller' | 'buyer'
  const [myTasks,      setMyTasks]      = React.useState(null);   // my open tasks
  const [reminders,    setReminders]    = React.useState(null);   // Lost Deals follow-up nudges
  const [closings,     setClosings]     = React.useState(null);   // deal_closings stats (Closed deals card)

  // iPad (tablet) only: the five-up KPI strip and the two-column rows are
  // cramped in portrait / Split View, so the strip wraps (auto-fit, 150px
  // min) and the rows stack below a 900px-wide body. Measured on the body
  // itself, not the window — the sidebar takes its share. Desktop keeps the
  // fixed grids.
  // Snapshot, not useDevice(): desktop Home must not re-render on window
  // resizes. A tablet↔desktop switch reloads the page anyway.
  const homeTablet = !!(window.AtlasDevice && window.AtlasDevice.tablet);
  const homeBodyRef = React.useRef(null);
  const [homeW, setHomeW] = React.useState(null);
  React.useEffect(() => {
    const el = homeBodyRef.current;
    if (!homeTablet || !el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => { if (entries[0]) setHomeW(entries[0].contentRect.width); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [homeTablet]);
  const homeStack = homeTablet && homeW != null && homeW < 900;

  React.useEffect(() => {
    window.AtlasApi.agentWorkspaces()
      .then((ws) => setWorkspaces(Array.isArray(ws) ? ws : []))
      .catch(() => setWorkspaces([]));
  }, []);

  // Pipeline chart + headline EV come from the server-side aggregate —
  // it covers ALL active engagements (the old limit=1000 pull silently
  // truncated the chart) and is a few hundred bytes instead of ~2MB.
  // On-hold deals are excluded server-side: parked fees aren't pipeline.
  const loadPipeline = React.useCallback(() => {
    window.AtlasApi.dashboardPipeline()
      .then(setPipeline)
      .catch(() => setPipeline({ stages: [], total_active: 0, total_est_fees: 0, total_weighted_fees: 0 }));
  }, []);

  // Alerts feed: server-side "needs attention" filter (on hold quiet 6mo+ /
  // active + non-terminal quiet 90d+). attention_sort=recent returns the
  // most-recently-active qualifiers first — the deals that only just went
  // quiet — so the urgency ranking below has the warm ones to work with
  // even when far more than 50 deals qualify. Exited deals never qualify
  // (server rule): an exited deal already sits on the Exited tab.
  // Sellers/Buyers is a server-side split, not a client filter: a blast
  // touches dozens of buyer shells at once, so a mixed fetch could be all
  // shells and starve the seller list (or vice versa).
  const sideParams = dealSide === 'buyer'
    ? { engagement_type: 'buyer_buyside' }
    : { exclude_engagement_type: 'buyer_buyside' };

  const loadAlerts = React.useCallback(() => {
    setAlertDeals(null);
    window.AtlasApi.listEngagements({
      my_team: true, include_archived: false, attention_only: true,
      attention_sort: 'recent', limit: 50,
      ...sideParams,
    }).then((res) => {
      setAlertDeals(((res && res.items) || []).map(window.AtlasData.__adaptEngagement));
    }).catch(() => setAlertDeals([]));
  }, [dealSide]);

  // "Recently active" tab: ongoing deals (not archived/exited) ordered by
  // latest touch — stage moves + linked notes — so a call logged an hour
  // ago outranks yesterday's stage move.
  const loadRecent = React.useCallback(() => {
    setRecentDeals(null);
    window.AtlasApi.listEngagements({
      my_team: true, include_archived: false, active_only: true,
      sort: 'recent_activity', limit: 8,
      ...sideParams,
    }).then((res) => {
      setRecentDeals(((res && res.items) || []).map(window.AtlasData.__adaptEngagement));
    }).catch(() => setRecentDeals([]));
  }, [dealSide]);

  const loadTasks = React.useCallback(() => {
    if (!activeUser?.id) { setMyTasks([]); return; }
    window.AtlasApi.listTasks({ assigned_to: activeUser.id, status: 'open', limit: 100 })
      .then((res) => setMyTasks((res && res.items) || []))
      .catch(() => setMyTasks([]));
  }, [activeUser?.id]);

  React.useEffect(() => {
    loadPipeline(); loadTasks();
    window.AtlasApi.closingsStats().then((r) => setClosings(r || {})).catch(() => setClosings({}));
    window.AtlasApi.dashboardReminders()
      .then((r) => setReminders(r || { total: 0, findings: [] }))
      .catch(() => setReminders({ total: 0, findings: [] }));
  }, [loadPipeline, loadTasks]);

  // The two deal feeds refetch whenever the Sellers/Buyers side flips
  // (loadAlerts/loadRecent close over dealSide).
  React.useEffect(() => { loadAlerts(); loadRecent(); }, [loadAlerts, loadRecent]);

  React.useEffect(() => {
    const refresh = () => { loadPipeline(); loadAlerts(); loadRecent(); };
    window.addEventListener('deal-updated', refresh);
    window.addEventListener('task-created', loadTasks);
    return () => {
      window.removeEventListener('deal-updated', refresh);
      window.removeEventListener('task-created', loadTasks);
    };
  }, [loadPipeline, loadAlerts, loadRecent, loadTasks]);

  const aggBySlug = React.useMemo(() => {
    const m = {};
    (pipeline?.stages || []).forEach((s) => { m[s.slug] = s; });
    return m;
  }, [pipeline]);

  // Metrics — prefer the cheap counts endpoint, fall back to the aggregate.
  // The headline is the probability-weighted pipeline (each deal's fees ×
  // its stage's probability_pct); the raw sum stays in the caption so the
  // discount is visible rather than silent.
  const breakdown = counts?.engagement_breakdown || {};
  const totalEV  = pipeline?.total_weighted_fees || 0;
  const totalRaw = pipeline?.total_est_fees || 0;
  const onHoldN  = breakdown.on_hold  ?? 0;
  const exitedN  = breakdown.exited   ?? 0;
  const mineN    = breakdown.mine     ?? 0;
  // The pipeline aggregate is scoped by role server-side ('self' for a
  // dealmaker, 'team' for an associate, 'all' under the All-teams toggle), so
  // its deal count has to come from the SAME response as its fee total —
  // breakdown.active is always team-wide and would caption a dealmaker's
  // $ with the whole team's deal count.
  const pipeScope = pipeline?.scope || 'team';
  const activeN   = pipeline?.total_active ?? 0;
  const SCOPE_NOTE = { self: 'yours', team: 'your team', all: 'all teams' };
  // 'self' means the $ is exactly the Mine tab's live deals; the wider scopes
  // are the Active tab. Either way, fees-descending — the number is a sum, so
  // the rows that make it up belong at the top.
  const pipeTab = pipeScope === 'self' ? 'mine' : 'active';

  // Task buckets — overdue first, then by due date (the server already
  // sorts due_date asc; overdue rows are simply the ones before today).
  const dayMs = 1000 * 60 * 60 * 24;
  const taskRows = React.useMemo(() => {
    if (!myTasks) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return myTasks.map((t) => {
      const due = new Date(`${t.due_date}T00:00:00`);
      const days = Math.round((due.getTime() - today.getTime()) / dayMs);
      return { t, days };
    }).sort((a, b) => a.days - b.days);
  }, [myTasks]);
  const overdueN = (taskRows || []).filter((r) => r.days < 0).length;

  // "Needs attention" — mirrors the server's _attention_filter thresholds
  // (routes/engagements.py): active deals alert after 90d without a touch
  // (stage move or linked note), on-hold ones after 6 months.
  //
  // Ranking is by URGENCY, not by age. Oldest-first buried the feed under
  // deals nobody had touched in years while a deal that went quiet last
  // week — still warm, still salvageable — never surfaced. Rank by how
  // recently the deal crossed its staleness threshold (just-crossed first).
  // Exited deals never appear here (excluded server-side; the d.stale
  // filter below is defense in depth) — exit triage lives in Tasks/Inbox.
  const ACTIVE_STALE_DAYS = 90;
  const ON_HOLD_STALE_DAYS = 180;
  // Buyer shells are named after the buyer; "Buyer › Seller deal" says
  // which pursuit the row actually is. Their real funnel stage is dcStage
  // (the mapped seller stage on a shell is a force-fit — see deal_detail);
  // strip DealCloud's "3. " ordering prefix.
  const dealLabel = (d) => (d.type === 'buyer_buyside' && d.sellerEngagementName)
    ? `${d.name} › ${d.sellerEngagementName}` : d.name;
  const dealStage = (d) => (d.type === 'buyer_buyside' && d.dcStage)
    ? d.dcStage.replace(/^\d+\.\s*/, '') : (d.stageName || d.stage);

  // Latest touch on a deal = max(stage move, linked-note activity).
  const touchOf = (d) => {
    const stamps = [d._raw?.last_stage_update, d.lastActivityAt]
      .filter(Boolean)
      .map((iso) => new Date(iso).getTime())
      .filter((t) => !Number.isNaN(t));
    return stamps.length ? Math.max(...stamps) : null;
  };
  const needsAttention = React.useMemo(() => {
    if (!alertDeals) return [];
    const now = Date.now();
    return alertDeals
      .map(d => { const t = touchOf(d); return { d, lastTouch: t, age: t != null ? (now - t) / dayMs : null }; })
      .filter(({ d, age }) => !d.stale && age != null && age >= (d.onHold ? ON_HOLD_STALE_DAYS : ACTIVE_STALE_DAYS))
      .map((x) => ({
        ...x,
        daysPast: x.age - (x.d.onHold ? ON_HOLD_STALE_DAYS : ACTIVE_STALE_DAYS),
      }))
      .sort((a, b) => a.daysPast - b.daysPast)
      .slice(0, 6);
  }, [alertDeals]);

  const rowBtnStyle = (i) => ({
    display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px', width: '100%',
    background: 'transparent', border: 'none', cursor: 'pointer',
    borderTop: i === 0 ? 'none' : '1px solid var(--border)', textAlign: 'left',
  });
  const hoverOn  = (e) => e.currentTarget.style.background = 'var(--surface-2)';
  const hoverOff = (e) => e.currentTarget.style.background = 'transparent';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Home']}
        title={`Good morning, ${(activeUser?.first_name || activeUser?.full_name || 'there').split(' ')[0]}`}
        meta={`${counts?.engagements ?? '—'} engagements · ${counts?.companies ?? '—'} companies`}
      />

      <div ref={homeBodyRef} style={{ flex: 1, overflow: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Strip metrics. Each card has its own loading source:
            - Pipeline EV depends on the pipeline aggregate fetch
            - On hold / Mine / Exited depend on the cheaper counts endpoint
            Showing skeleton blocks instead of "—" or "0" avoids the brief
            misleading flash of zeros before the real numbers arrive. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12,
                      // iPad: reflow instead of five squeezed cards.
                      ...(homeTablet ? { gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' } : null) }}>
          {[
            // This card's number is a SUM, not a count, so it opens
            // fees-descending: the rows that make up the $ are at the top.
            // The other three are counts — recency order is the useful one.
            { l: 'Pipeline (weighted fees)', v: fmtMoney(totalEV),  s: `${fmtMoney(totalRaw)} unweighted · ${activeN.toLocaleString()} active · ${SCOPE_NOTE[pipeScope] || 'your team'}`, loading: pipeline === null, tab: pipeTab, sort: { key: 'fees', dir: 'desc' }, drill: `Open ${pipeScope === 'self' ? 'your' : 'these'} active engagements, largest fees first` },
            { l: 'On hold',              v: onHoldN,             s: 'parked — not in pipeline fees',       loading: counts === null, tab: 'held',   drill: 'Open the parked engagements' },
            { l: 'Mine',                 v: mineN,               s: myName ? `assigned to ${myName.split(' ')[0]}` : '—', loading: counts === null, tab: 'mine',   drill: 'Open the engagements assigned to you' },
            { l: 'Exited',               v: exitedN,             s: 'closed-lost / dropped',               loading: counts === null, tab: 'exited', drill: 'Open the exited engagements' },
            // Closed deals (202609301300): what we closed at — count, median EBITDA multiple, fees. Opens the Won tab and its strip.
            { l: 'Closed deals',         v: closings && closings.overall ? closings.overall.n.toLocaleString() : '0',
              s: closings && closings.overall ? `${closings.overall.med_ebitda_x != null ? Number(closings.overall.med_ebitda_x).toFixed(1) + '× EBITDA median · ' : ''}${fmtMoney(closings.overall.fees || 0)} fees` : 'nothing recorded yet',
              loading: closings === null, tab: 'won', drill: 'Open the closed deals and what they closed at' },
          ].map((k) => {
            // Each card drills into the Engagements tab it counts. Rendered as
            // a <button> (not a div with onClick) so it's keyboard-reachable;
            // disabled while the number is still loading so a click can't land
            // on a card whose meaning isn't on screen yet.
            const clickable = Boolean(onOpenDealsTab) && !k.loading;
            return (
            <button
              key={k.l}
              type="button"
              onClick={clickable ? () => onOpenDealsTab(k.tab, k.sort || null) : undefined}
              disabled={!clickable}
              title={clickable ? k.drill : undefined}
              style={{
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
                padding: '12px 14px', textAlign: 'left', font: 'inherit', color: 'inherit',
                cursor: clickable ? 'pointer' : 'default',
              }}
              onMouseEnter={clickable ? (e) => e.currentTarget.style.borderColor = 'var(--border-strong)' : undefined}
              onMouseLeave={clickable ? (e) => e.currentTarget.style.borderColor = 'var(--border)' : undefined}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>{k.l}</span>
                {clickable && <Icon.ChevronR size={11} color="var(--text-4)" />}
              </div>
              {k.loading ? (
                <>
                  <div style={{ marginTop: 4, height: 26, display: 'flex', alignItems: 'center' }}>
                    <Skeleton w={90} h={22} />
                  </div>
                  <div style={{ marginTop: 2 }}>
                    <Skeleton w={130} h={11} />
                  </div>
                </>
              ) : (
                <>
                  <div className="num" style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '-0.015em', marginTop: 2 }}>{k.v}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{k.s}</div>
                </>
              )}
            </button>
            );
          })}
        </div>

        {/* Follow-up reminders — the Lost Deals Finder's new email-shaped
            findings for this user (unanswered inbound / gone quiet). Slim
            strip; hidden entirely when there's nothing new. */}
        {reminders && reminders.total > 0 && (
          <button
            onClick={() => setView('lost-deals')}
            title="Open Lost Deals to review, draft replies, or dismiss"
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
              cursor: 'pointer', textAlign: 'left',
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--border-strong)'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
          >
            <span style={{ width: 24, height: 24, borderRadius: 7, background: HUE.amber.bg, color: HUE.amber.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon.Mail size={13} />
            </span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--text)' }}>
              <b>{reminders.total} suggested follow-up{reminders.total === 1 ? '' : 's'}</b>
              <span style={{ color: 'var(--text-3)' }}>
                {' '}from your inbox
                {reminders.findings[0] ? ` — ${reminders.findings[0].correspondent_email} (${REMINDER_LABEL[reminders.findings[0].category] || reminders.findings[0].category})${reminders.total > 1 ? ', …' : ''}` : ''}
              </span>
            </span>
            <span style={{ fontSize: 11.5, color: 'var(--accent)', flexShrink: 0 }}>Review</span>
            <Icon.ChevronR size={12} color="var(--text-4)" />
          </button>
        )}

        {/* Signals strip (202609301900): what is due on the reader's book —
            revisit dates that have come, deals gone dark, nearby sales. */}
        {window.HomeSignalsStrip && <window.HomeSignalsStrip setView={setView} />}

        {/* Row 1 — what to act on now: my tasks + deals needing attention. */}
        <div style={{ display: 'grid', gridTemplateColumns: homeStack ? 'minmax(0, 1fr)' : '1fr 1.2fr', gap: 14 }}>
          <Section
            title={myTasks === null ? 'My tasks' : `My tasks · ${taskRows.length}${overdueN ? ` · ${overdueN} overdue` : ''}`}
            action={<Btn size="xs" variant="ghost" onClick={() => setView('inbox')} icon={<Icon.External size={11} />}>View all</Btn>}
          >
            <div>
              {myTasks === null && <SkeletonRows rows={3} variant="generic" />}
              {taskRows && taskRows.length === 0 && (
                <div style={{ padding: '16px 14px', fontSize: 12, color: 'var(--text-3)', lineHeight: 1.6 }}>
                  No open tasks. Create one from any deal page, or with ⌘K.
                </div>
              )}
              {(taskRows || []).slice(0, 6).map(({ t, days }, i) => {
                const dueHue  = days < 0 ? 'rose' : days === 0 ? 'amber' : 'slate';
                const dueText = days < 0 ? `Overdue ${-days}d` : days === 0 ? 'Due today' : days === 1 ? 'Due tomorrow' : `Due in ${days}d`;
                return (
                  <button key={t.id} onClick={() => setView('inbox')} style={rowBtnStyle(i)}
                    onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.engagement_name || t.task_type.replace(/_/g, ' ')}
                        {(t.priority === 'urgent' || t.priority === 'high') ? ` · ${t.priority} priority` : ''}
                      </div>
                    </div>
                    <Pill hue={dueHue} sz="sm">{dueText}</Pill>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Deals panel — two tabs sharing one card:
                Recently active (default) — ongoing deals, latest touch first.
                Needs attention — quiet-deal alerts, urgency-ranked. */}
          <Section title={(
            <span style={{ display: 'inline-flex', gap: 4 }}>
              {[
                ['active', 'Recently active'],
                ['attention', alertDeals === null ? 'Needs attention' : `Needs attention · ${needsAttention.length}`],
              ].map(([key, label]) => (
                <button key={key} onClick={() => setDealTab(key)}
                  style={{
                    padding: '3px 8px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    fontSize: 12.5, fontWeight: 600,
                    background: dealTab === key ? 'var(--surface-2)' : 'transparent',
                    color: dealTab === key ? 'var(--text)' : 'var(--text-3)',
                  }}>{label}</button>
              ))}
            </span>
          )}
            action={(
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {/* Seller deals vs per-buyer pursuit shells (buyer_buyside) —
                    applies to both tabs. */}
                <span style={{ display: 'inline-flex', background: 'var(--surface-2)', borderRadius: 6, padding: 2 }}>
                  {[['seller', 'Sellers'], ['buyer', 'Buyers']].map(([key, label]) => (
                    <button key={key} onClick={() => setDealSide(key)}
                      style={{
                        padding: '2px 8px', borderRadius: 5, border: 'none', cursor: 'pointer',
                        fontSize: 11, fontWeight: 600,
                        background: dealSide === key ? 'var(--surface)' : 'transparent',
                        color: dealSide === key ? 'var(--text)' : 'var(--text-3)',
                        boxShadow: dealSide === key ? '0 0 0 1px var(--border)' : 'none',
                      }}>{label}</button>
                  ))}
                </span>
                {dealTab === 'active' && (
                  <Btn size="xs" variant="ghost" onClick={() => setView('deals')} icon={<Icon.External size={11} />}>View all</Btn>
                )}
              </span>
            )}
          >
            {dealTab === 'active' && (
              <div>
                {recentDeals === null && <SkeletonRows rows={3} variant="engagement" />}
                {recentDeals && recentDeals.length === 0 && (
                  <div style={{ padding: '16px 14px', fontSize: 12, color: 'var(--text-3)' }}>
                    No ongoing deals with activity yet.
                  </div>
                )}
                {(recentDeals || []).slice(0, 6).map((d, i) => {
                  const t = touchOf(d);
                  return (
                    <button key={d.id} onClick={() => { setView('deal'); setDealId(d.id); }} style={rowBtnStyle(i)}
                      onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dealLabel(d)}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {dealStage(d)} · <UserLink id={d._raw?.dealmaker_id} name={d.owner} style={{ color: 'var(--text-2)' }} />
                        </div>
                      </div>
                      {d.onHold && <Pill hue="amber" sz="sm">On hold</Pill>}
                      <Pill hue="slate" sz="sm">{t != null ? `Updated ${relativeTime(new Date(t).toISOString())}` : 'Updated —'}</Pill>
                      <Icon.ChevronR size={12} color="var(--text-4)" />
                    </button>
                  );
                })}
              </div>
            )}
            {dealTab === 'attention' && (
              <div>
                {alertDeals === null && <SkeletonRows rows={3} variant="engagement" />}
                {alertDeals && needsAttention.length === 0 && (
                  <div style={{ padding: '16px 14px', fontSize: 12, color: 'var(--text-3)' }}>
                    Nothing quiet &gt; {ACTIVE_STALE_DAYS} days or on hold without an update in 6mo+. Pipeline's clean.
                  </div>
                )}
                {needsAttention.map(({ d, age, lastTouch }, i) => {
                  let reason, hue;
                  if (d.onHold) { reason = `On hold · quiet ${Math.round(age)}d`; hue = 'amber'; }
                  else          { reason = `Went quiet ${Math.round(age)}d ago`; hue = 'slate'; }
                  return (
                    <button key={d.id} onClick={() => { setView('deal'); setDealId(d.id); }} style={rowBtnStyle(i)}
                      onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dealLabel(d)}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {dealStage(d)} · last activity {lastTouch != null ? relativeTime(new Date(lastTouch).toISOString()) : '—'} · <UserLink id={d._raw?.dealmaker_id} name={d.owner} style={{ color: 'var(--text-2)' }} />
                        </div>
                      </div>
                      <Pill hue={hue} dot sz="sm">{reason}</Pill>
                      <Icon.ChevronR size={12} color="var(--text-4)" />
                    </button>
                  );
                })}
              </div>
            )}
          </Section>
        </div>

        {/* Row 2 — context: live agent workspaces + the pipeline chart. */}
        <div style={{ display: 'grid', gridTemplateColumns: homeStack ? 'minmax(0, 1fr)' : '1.4fr 1fr', gap: 14 }}>
          <Section
            title="Agent workspaces"
            action={<Btn size="xs" variant="ghost" onClick={() => setView('agents')} icon={<Icon.External size={11} />}>View all</Btn>}
          >
            <div>
              {workspaces === null && (
                <SkeletonRows rows={3} variant="generic" />
              )}
              {workspaces && workspaces.length === 0 && (
                <div style={{ padding: '16px 14px', fontSize: 12, color: 'var(--text-3)', lineHeight: 1.6 }}>
                  No agent workspaces yet. Open any deal and tap <b>Run agent</b> to start one.
                </div>
              )}
              {workspaces && workspaces.slice(0, 5).map((w, i) => {
                const status = w.latest_run_status || (w.active_run_count > 0 ? 'running' : 'idle');
                const sHue = status === 'running' ? 'blue'
                           : status === 'completed' ? 'green'
                           : status === 'failed' ? 'rose'
                           : 'slate';
                return (
                  <div key={w.id}
                    onClick={() => setWorkspaceId && setWorkspaceId(w.id)}
                    title="Open workspace — see what the agent did"
                    style={{ display: 'flex', gap: 10, padding: '10px 14px', borderTop: i === 0 ? 'none' : '1px solid var(--border)', alignItems: 'flex-start', cursor: 'pointer' }}
                    onMouseEnter={hoverOn}
                    onMouseLeave={hoverOff}
                  >
                    <span style={{ width: 24, height: 24, borderRadius: 7, background: HUE.blue.bg, color: HUE.blue.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon.Bot size={13} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: 'var(--text)' }}>{w.name}</span>
                        <Pill hue={sHue} dot sz="sm">{status}</Pill>
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>
                        {w.lead_agent_key.replace(/-/g, ' ')} · {w.focus_type}
                      </div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 3, fontFamily: 'var(--font-mono)' }}>
                        {w.run_count} runs · {w.artifact_count} artifacts · {w.handoff_count} handoffs
                      </div>
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--text-3)', flexShrink: 0 }}>
                      {relativeTime(w.updated_at)}
                    </span>
                  </div>
                );
              })}
            </div>
          </Section>

          {/* Pipeline by stage — from the server-side aggregate */}
          <Section title="Pipeline by stage (weighted)" action={<Btn size="xs" variant="ghost" onClick={() => setView('deals')}>Open</Btn>}>
            <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pipeline === null && (
                /* 6 rows of skeleton bars matching the real per-stage row
                   layout: stage label · progress bar · money. Widths vary
                   so it looks like real data, not a uniform stripe. */
                <>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 90px', alignItems: 'center', gap: 10 }}>
                      <Skeleton w={`${75 - i * 6}%`} h={11} />
                      <Skeleton w="100%" h={10} style={{ borderRadius: 4 }} />
                      <Skeleton w={`${60 - (i % 3) * 10}%`} h={11} style={{ marginLeft: 'auto' }} />
                    </div>
                  ))}
                </>
              )}
              {/* Sellside rows (082) appear only once a deal actually sits on
                  one — the buyside funnel is the default and 10 extra empty
                  rows would double the panel for nothing. */}
              {pipeline && (stages || [])
                .filter(s => !s.is_terminal && s.category !== 'buyer')
                .filter(s => s.category !== 'sellside' || (aggBySlug[s.slug]?.count || 0) > 0)
                .map((s) => {
                const agg = aggBySlug[s.slug] || { count: 0, est_fees: 0, weighted_fees: 0 };
                const ev = agg.weighted_fees || 0;
                const stageEvMax = Math.max(1, ...(stages || []).filter(x => !x.is_terminal && x.category !== 'buyer').map((x) =>
                  (aggBySlug[x.slug]?.weighted_fees) || 0));
                const pct = stageEvMax > 0 ? (ev / stageEvMax) * 100 : 0;
                const h = HUE[DASHBOARD_STAGE_HUE[s.slug] || 'slate'];
                const clickable = !!onOpenStage;
                return (
                  <div key={s.slug}
                    onClick={clickable ? () => onOpenStage(s.slug, pipeTab) : undefined}
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenStage(s.slug, pipeTab); } } : undefined}
                    title={`${fmtMoney(agg.est_fees || 0)} unweighted × ${Number(s.probability_pct) || 0}%${clickable ? ` — view ${agg.count} ${s.name} deal${agg.count === 1 ? '' : 's'}` : ''}`}
                    style={{
                      display: 'grid', gridTemplateColumns: '160px 1fr 90px', alignItems: 'center', gap: 10,
                      cursor: clickable ? 'pointer' : 'default', borderRadius: 6,
                      margin: '0 -6px', padding: '3px 6px', transition: 'background .12s',
                    }}
                    onMouseEnter={clickable ? (e) => e.currentTarget.style.background = 'var(--surface-2)' : undefined}
                    onMouseLeave={clickable ? (e) => e.currentTarget.style.background = 'transparent' : undefined}
                  >
                    <span style={{ fontSize: 12, color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 7, height: 7, borderRadius: 999, background: h.dot }} />
                      {s.name}
                      <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>·</span>
                      <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{agg.count}</span>
                    </span>
                    <div style={{ height: 10, background: 'var(--surface-2)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: h.dot, opacity: 0.55 }} />
                    </div>
                    <span className="num" style={{ fontSize: 11.5, color: 'var(--text-2)', textAlign: 'right' }}>{fmtMoney(ev)}</span>
                  </div>
                );
              })}
              {pipeline && (!stages || stages.length === 0) && (
                /* aggregate loaded but stages still missing — same skeleton
                   as above so the panel doesn't flash from skeleton →
                   empty text → real bars. */
                <>
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 90px', alignItems: 'center', gap: 10 }}>
                      <Skeleton w={`${70 - i * 8}%`} h={11} />
                      <Skeleton w="100%" h={10} style={{ borderRadius: 4 }} />
                      <Skeleton w={`${55 - (i % 2) * 12}%`} h={11} style={{ marginLeft: 'auto' }} />
                    </div>
                  ))}
                </>
              )}
            </div>
          </Section>
        </div>

        {/* Row 3 — the market: where buyers move vs where we call, what
            sellers and buyers say, multiples said / indicated / closed. */}
        {window.MarketBand && <window.MarketBand setView={setView} onOpenDealsTab={onOpenDealsTab} />}

        {/* Row 4 — the user's pinned analytics dashboard (full-width embed). */}
        <HomeDashboard setView={setView} />
      </div>
    </div>
  );
};

window.Home = Home;
